import logger from 'js/app/loggerSingleton';

import { SEARCH_MAIN_INDEX_ID } from 'bundles/search-common/constants';
import { useSearch } from 'bundles/search-common/providers/SearchContext';

type FacetMapping = {
  [key: string]: string | string[];
};

type FacetCountsResult<T extends FacetMapping> = {
  [K in keyof T]: number;
} & {
  isLoading: boolean;
};

export const useSearchFacetCounts = <T extends FacetMapping>(
  facetName: string,
  valueMapping: T
): FacetCountsResult<T> => {
  const searchProviderContext = useSearch();

  if (!searchProviderContext) {
    logger.warn('useSearchFacetCounts expected SearchProvider');
  }

  const { getSearchResults, isLoading } = searchProviderContext;
  const searchData = getSearchResults(SEARCH_MAIN_INDEX_ID);
  const { facets: searchFacets } = searchData || {};
  const facet = searchFacets?.find((searchFacet) => searchFacet.name === facetName);

  const initialCounts = Object.keys(valueMapping).reduce((acc, key) => {
    acc[key] = 0;
    return acc;
  }, {} as { [key: string]: number });

  const validValues = Object.values(valueMapping).flat();

  const facetCounts =
    facet?.valuesAndCounts?.reduce((acc, curr) => {
      if (curr.count > 0 && validValues.includes(curr.value)) {
        for (const [mappingKey, mappingValues] of Object.entries(valueMapping)) {
          const valuesArray = Array.isArray(mappingValues) ? mappingValues : [mappingValues];
          if (valuesArray.includes(curr.value)) {
            const currentCount = acc[mappingKey];
            if (currentCount !== undefined) {
              acc[mappingKey] = currentCount + curr.count;
            }
            break;
          }
        }
      }
      return acc;
    }, initialCounts) || initialCounts;

  return { ...facetCounts, isLoading: Boolean(isLoading) } as FacetCountsResult<T>;
};
